import React from 'react';
import { Save, AlertTriangle, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@hts/ui';
import { useGet, apiPatch } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { ModeBadge } from '../../components/ui/StatusBadge.js';
import { relativeTime } from '../../lib/format.js';
import type { BoardPolicy, TriageMode } from '../../api/types.js';

export function BoardPoliciesPage() {
  const { data, loading, error, refetch } = useGet<{ items: Array<BoardPolicy & { updatedByName?: string }> }>('/api/v1/boards/policies');
  const auth = useAuth();
  const role = auth.status === 'authenticated'
    ? auth.user.memberships.find(m => m.tenantId === auth.user.activeTenantId)?.role
    : undefined;
  // PATCH /boards/policies/:id is admin/owner only; everyone else gets a read-only view.
  const canEdit = role === 'admin' || role === 'owner';
  const [editing, setEditing] = React.useState<Record<string, BoardPolicy>>({});
  const [saving, setSaving] = React.useState<Record<string, boolean>>({});
  const [saved, setSaved] = React.useState<Record<string, boolean>>({});

  const policies = data?.items ?? [];

  function getPolicy(id: string): BoardPolicy {
    return editing[id] ?? policies.find(p => p.id === id)!;
  }

  function updatePolicy(id: string, updates: Partial<BoardPolicy>) {
    const base = editing[id] ?? policies.find(p => p.id === id)!;
    setEditing(prev => ({ ...prev, [id]: { ...base, ...updates } }));
  }

  function updateWeight(id: string, key: keyof BoardPolicy['weights'], value: number) {
    const base = editing[id] ?? policies.find(p => p.id === id)!;
    setEditing(prev => ({
      ...prev,
      [id]: { ...base, weights: { ...base.weights, [key]: value } },
    }));
  }

  async function savePolicy(id: string) {
    const pol = editing[id];
    if (!pol) return;
    setSaving(prev => ({ ...prev, [id]: true }));
    try {
      await apiPatch(`/api/v1/boards/policies/${id}`, {
        mode: pol.mode,
        enabled: pol.enabled,
        autoThreshold: pol.autoThreshold,
        weights: pol.weights,
      });
      setSaved(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [id]: false })), 2500);
      setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
      refetch();
    } catch (e) {
      toast.error(`Couldn't save ${pol.boardName || 'policy'}: ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setSaving(prev => ({ ...prev, [id]: false }));
    }
  }

  function discard(id: string) {
    setEditing(prev => { const n = { ...prev }; delete n[id]; return n; });
  }

  const MODES: TriageMode[] = ['shadow', 'recommend', 'auto'];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Boards & Policies</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">
          Configure AI dispatch mode, confidence thresholds, and scoring weights per board.
          {!canEdit && auth.status === 'authenticated' && ' Read-only: admin or owner role required to change policies.'}
        </p>
      </div>

      {/* Mode legend */}
      <div className="flex items-start gap-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ModeBadge mode="shadow" />
          <span className="text-xs text-[hsl(var(--content-subtle))]">AI analyzes and logs — no UI action required</span>
        </div>
        <div className="flex items-center gap-2">
          <ModeBadge mode="recommend" />
          <span className="text-xs text-[hsl(var(--content-subtle))]">Shows top-3 suggestions — human approves</span>
        </div>
        <div className="flex items-center gap-2">
          <ModeBadge mode="auto" />
          <span className="text-xs text-[hsl(var(--content-subtle))]">Auto-dispatches if confidence ≥ threshold</span>
        </div>
      </div>

      {loading && !data ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={5} />)}
        </div>
      ) : error ? (
        <ErrorState message={`Failed to load board policies (${error}).`} onRetry={refetch} />
      ) : policies.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No board policies yet"
          description="A default shadow-mode policy is created for each board when the ConnectWise board sync runs. Check the ConnectWise integration under Admin › Integrations."
        />
      ) : (
        <div className="space-y-4">
          {policies.map(policy => {
            const pol = getPolicy(policy.id);
            const isDirty = !!editing[policy.id];
            const isSaving = saving[policy.id];
            const isSaved = saved[policy.id];
            const totalWeight = Object.values(pol.weights).reduce((s, v) => s + v, 0);
            const weightOk = Math.abs(totalWeight - 1.0) < 0.01;

            return (
              <div key={policy.id} className={cn(
                'rounded-2xl border bg-[hsl(var(--surface))] shadow-sm overflow-hidden transition-all',
                isDirty ? 'border-brand-400' : 'border-[hsl(var(--border))]'
              )}>
                {/* Board header */}
                <div className="px-5 py-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-[hsl(var(--content))]">{pol.boardName}</h3>
                      <ModeBadge mode={pol.mode} />
                      {!pol.enabled && (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 font-semibold">
                          <AlertTriangle className="h-3 w-3" /> Disabled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[hsl(var(--content-placeholder))] mt-0.5">
                      Last updated: {relativeTime(pol.updatedAt)} by {policy.updatedByName ?? pol.updatedBy ?? 'system'}
                    </p>
                  </div>

                  {/* Kill switch */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[hsl(var(--content-subtle))]">AI enabled</span>
                    <button
                      onClick={() => updatePolicy(policy.id, { enabled: !pol.enabled })}
                      disabled={!canEdit}
                      aria-label={`AI enabled for ${pol.boardName}`}
                      className={cn(
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
                        pol.enabled ? 'bg-brand-600' : 'bg-gray-300 dark:bg-gray-600'
                      )}
                      role="switch"
                      aria-checked={pol.enabled}
                    >
                      <span className={cn(
                        'inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        pol.enabled ? 'translate-x-5' : 'translate-x-0'
                      )} />
                    </button>
                  </div>

                  {isDirty && (
                    <button
                      onClick={() => discard(policy.id)}
                      disabled={isSaving}
                      className="flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] px-3 py-1.5 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface))] disabled:opacity-50 transition-colors"
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                      Discard
                    </button>
                  )}
                  {isDirty && (
                    <button
                      onClick={() => savePolicy(policy.id)}
                      disabled={isSaving || !weightOk}
                      className="flex items-center gap-1.5 rounded-xl bg-brand-600 text-white px-3 py-1.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {isSaving ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  {isSaved && (
                    <span className="text-sm text-green-600 font-medium">✓ Saved</span>
                  )}
                </div>

                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Mode selector */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">Dispatch Mode</h4>
                    <div className="flex gap-2">
                      {MODES.map(mode => (
                        <button
                          key={mode}
                          onClick={() => updatePolicy(policy.id, { mode })}
                          disabled={!canEdit || !pol.enabled}
                          title={!pol.enabled ? 'AI is disabled for this board (kill switch) — mode is forced to shadow' : undefined}
                          className={cn(
                            'flex-1 px-3 py-2 rounded-xl text-xs font-semibold border transition-all capitalize disabled:cursor-not-allowed disabled:opacity-60',
                            pol.mode === mode
                              ? mode === 'shadow' ? 'border-slate-400 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
                                : mode === 'recommend' ? 'border-blue-400 bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                : 'border-purple-400 bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                              : 'border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:border-[hsl(var(--border-strong))] hover:bg-[hsl(var(--surface-subtle))]'
                          )}
                        >
                          {mode}
                        </button>
                      ))}
                    </div>

                    {/* Auto threshold */}
                    {pol.mode === 'auto' && (
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-[hsl(var(--content-subtle))]">Auto-dispatch threshold</span>
                          <span className="text-xs font-bold text-[hsl(var(--content))]">{(pol.autoThreshold * 100).toFixed(0)}%</span>
                        </div>
                        <input
                          type="range"
                          min="0.5"
                          max="1.0"
                          step="0.05"
                          value={pol.autoThreshold}
                          onChange={e => updatePolicy(policy.id, { autoThreshold: parseFloat(e.target.value) })}
                          disabled={!canEdit}
                          className="w-full accent-brand-600"
                        />
                        <div className="flex justify-between text-[10px] text-[hsl(var(--content-placeholder))]">
                          <span>50% (lenient)</span>
                          <span>100% (strict)</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Weight sliders */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">Score Weights</h4>
                      <span className={cn('text-xs font-bold', weightOk ? 'text-green-600' : 'text-red-600')}>
                        Total: {(totalWeight * 100).toFixed(0)}% {weightOk ? '✓' : '⚠'}
                      </span>
                    </div>
                    {(Object.entries(pol.weights) as [keyof BoardPolicy['weights'], number][]).map(([key, val]) => (
                      <div key={key} className="space-y-0.5">
                        <div className="flex justify-between">
                          <span className="text-xs text-[hsl(var(--content-subtle))] capitalize">{key}</span>
                          <span className="text-xs font-mono font-medium text-[hsl(var(--content))]">{(val * 100).toFixed(0)}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="0.6"
                          step="0.05"
                          value={val}
                          onChange={e => updateWeight(policy.id, key, parseFloat(e.target.value))}
                          disabled={!canEdit}
                          aria-label={`${key} weight`}
                          className="w-full accent-brand-600"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
